import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Trans } from "@lingui/macro";
import { Text } from "@otrium/atoms";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import { useProductBySlug } from "src/hooks/useProductBySlug";
import { ProductVariation } from "src/types/graphql.d";
import { useAppActions } from "src/hooks/stores/use-app-store";
import { Button } from "src/atoms/Button";
import { CloseButton } from "src/atoms/CloseButton";
import { Price } from "src/atoms/Price";
import { ScreenLoader } from "src/molecules/ScreenLoader";
import { ProductSizeSelector } from "src/molecules/ProductSizeSelector";
import { SmallSizeGuideHeader } from "src/organisms/SmallSizeGuideHeader";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { useCartData } from "src/hooks/stores/use-cart-store";
import { useIncreaseCartItem } from "src/hooks/useIncreaseCartItem";
import { useSegment } from "src/hooks/useSegment";
import { useMiscellaneous } from "src/hooks/useMiscellaneous";
import { trackProductAddedEvent } from "src/tracking/events/product-added";
import { useSession } from "next-auth/react";
import { useSizeSchemaFilter } from "src/hooks/useSizeSchemaFilter";
import { getCartInfo } from "src/utils/ct/cart-helper";
import {
  ModalContainer,
  ModalHeader,
  Content,
  Image,
  DescriptionContent,
  BrandName,
  ProductName,
  SizeContent,
  PriceContent,
} from "./WishListProductSelector.styled";

const WishListProductSelector = ({
  productSlug,
  onCloseModal,
  onOpenSizeGuide,
  onSizeSelectorOpen,
  onSizeSelectorClose,
}: {
  productSlug: string;
  onCloseModal: () => void;
  onOpenSizeGuide?: (sizeGuideText: string) => void;
  onSizeSelectorOpen?: () => void;
  onSizeSelectorClose?: () => void;
}): JSX.Element => {
  const [size, setSize] = useState<ProductVariation>();
  const [addToCartError, setAddToCartError] = useState<boolean>();
  const [selectedSizeSchema, setSelectedSizeSchema] = useState<string>();
  const [isBackInStockPopupOpen, setIsBackInStockPopupOpen] = useState(false);
  const isSizeSelectorInitialized = useRef(false);

  const { isMobile } = useBreakpoints();
  const { setAddedProdToCart } = useAppActions();

  const { product, loading: productLoading } = useProductBySlug(productSlug);
  const { cart } = useCartData();
  const { data: miscellaneousData } = useMiscellaneous();

  const { segmentProductOutOfStock, segmentProductSizeSelected } = useSegment();
  const { data: session } = useSession();

  const { formatVariationValue } = useSizeSchemaFilter({
    variations: product?.variations || [],
    selectedSchema: selectedSizeSchema,
    onSchemaChange: setSelectedSizeSchema,
  });

  const { brandSizeFitSmallList = [], categoriesSizeFitSmallList = [] } =
    miscellaneousData?.miscellaneous?.pdp || {};

  const isSizeFitsSmall = useMemo(() => {
    if (!product) return false;
    const brandSlug = product.brand?.slug;
    const categorySlugs = product.translated_category_slugs;
    return (
      brandSizeFitSmallList?.includes(brandSlug) &&
      categorySlugs?.some((category: string) =>
        categoriesSizeFitSmallList?.includes(category)
      )
    );
  }, [product, brandSizeFitSmallList, categoriesSizeFitSmallList]);

  const availableSizes: ProductVariation[] =
    product?.variations?.filter(
      (variation: ProductVariation) => variation.stock_quantity > 0
    ) || [];

  const selectedSize =
    size?.size ||
    (availableSizes.length === 1 ? (availableSizes[0]?.size as string) : "");

  const productImage = (product?.images as string[])?.[0] || "";

  const { increaseCartItem, loading, error } = useIncreaseCartItem({
    onCompleted: (resultData) => {
      onCloseModal();

      if (product && resultData.updateMyCart) {
        void trackProductAddedEvent({
          product,
          size: selectedSize,
          email: session?.user?.email,
        });
        setAddedProdToCart({
          productId: product.id,
          productName: product.name,
          productSlug: product.slug,
          brandName: product.brand.name,
          price: product.price,
          regularPrice: product.regular_price,
          image: product.images?.[0] as string,
          selectedSize: size
            ? formatVariationValue(
                size.id,
                size.size || "",
                product.size_schema || undefined
              )
            : selectedSize,
        });
      }
    },
  });

  useEffect(() => {
    if (!!addToCartError && product?.id) {
      void segmentProductOutOfStock(product, selectedSize);
    }
  }, [addToCartError, product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error) {
      setAddToCartError(true);
      onSizeSelectorClose?.();
    }
  }, [setAddToCartError, error, onSizeSelectorClose]);

  useEffect(() => {
    if (product && size) {
      void segmentProductSizeSelected(product, selectedSize);
    }
  }, [size]); // eslint-disable-line react-hooks/exhaustive-deps

  const sizeGuideText = useMemo(() => {
    if (!product) return "";
    const productGenderId = localeFix({
      gender: product.gender || undefined,
    });
    return (
      product.size_guide?.find(({ gender }) => gender === productGenderId)
        ?.text || ""
    );
  }, [product]);

  const handleOpenSizeGuide = useCallback(() => {
    if (onOpenSizeGuide) {
      onOpenSizeGuide(sizeGuideText);
    }
  }, [sizeGuideText, onOpenSizeGuide]);

  const dropdownHeader = (
    <SmallSizeGuideHeader m={3} onOpenSizeGuide={handleOpenSizeGuide} />
  );

  const handleSizeSelectorClose = useCallback(() => {
    onSizeSelectorClose?.();
    if (isMobile && !addToCartError) {
      onCloseModal();
      return;
    }

    if (!size) {
      onCloseModal();
    }
  }, [size, onSizeSelectorClose, onCloseModal, isMobile, addToCartError]);

  const handleSizeSelect = useCallback(
    (variationId: string) => {
      const selectedVariation = product?.variations?.find(
        (v) => v.id === variationId
      );
      if (selectedVariation) {
        setSize(selectedVariation);
      }
    },
    [product?.variations]
  );

  useEffect(() => {
    if (!isSizeSelectorInitialized.current && availableSizes.length === 1) {
      isSizeSelectorInitialized.current = true;
      handleSizeSelect(availableSizes[0].id);
    }
  }, [availableSizes, handleSizeSelect]);

  const handleAddToCart = useCallback(async (): Promise<boolean> => {
    if (!size?.id || !cart) {
      return false;
    }

    const item = cart.lineItems.find(
      (lineItem) => lineItem?.variant?.sku === size.id
    );
    const currentQuantity = (item?.quantity as number) || 0;
    const availableQuantity =
      item?.variant?.availability?.channels?.results.reduce(
        (sum, stockInfo) =>
          sum + (stockInfo.availability.availableQuantity as number),
        0
      );

    if (
      availableQuantity &&
      currentQuantity &&
      availableQuantity <= currentQuantity
    ) {
      setAddToCartError(true);
      onSizeSelectorClose?.();
      return false;
    } else {
      const result = await increaseCartItem(size.id, item, getCartInfo(cart));
      return !!result?.data;
    }
  }, [increaseCartItem, cart, size?.id]);

  if (!product && productLoading) {
    return <ScreenLoader />;
  }

  return (
    <ModalContainer
      data-testid="wishlist-modal-content-add-to-cart"
      isHidden={isBackInStockPopupOpen}
    >
      {product && !addToCartError && (
        <>
          <ModalHeader>
            <Text
              fontFamily="PlusJakartaSans"
              fontSize={20}
              fontWeight={600}
              color="tone.black"
            >
              <Trans>Add to bag</Trans>
            </Text>
            <CloseButton
              onClick={onCloseModal}
              iconSize={24}
              sx={{
                padding: 0,
                justifyContent: "end",
              }}
            />
          </ModalHeader>

          <Content css={{ alignItems: "start" }}>
            {productImage && <Image src={productImage} alt={product.name} />}
            <DescriptionContent>
              <BrandName>{product.brand.name}</BrandName>
              <ProductName>{product.name}</ProductName>
              {size && (
                <SizeContent>
                  <span>
                    <Trans>Size</Trans>
                  </span>
                  <span>
                    {size
                      ? formatVariationValue(
                          size.id,
                          size.size || "",
                          product.size_schema || undefined
                        )
                      : ""}
                  </span>
                </SizeContent>
              )}
              <PriceContent>
                <Price price={product.price} />
                <Text as="span" color="tone.anthracite">
                  <Price price={product.regular_price} />
                  {"*"}
                </Text>
              </PriceContent>
            </DescriptionContent>
          </Content>

          <Text
            fontSize={14}
            marginBottom={2}
            textDecoration="underline"
            onClick={handleOpenSizeGuide}
            sx={{ textAlign: "right", cursor: "pointer" }}
            data-testid="wishlist-size-guide-button"
          >
            <Trans>Size guide</Trans>
          </Text>

          <Box mb={2}>
            <ProductSizeSelector
              data-testid="wishlist-size-selector"
              product={product}
              selectedVariationId={size?.id}
              onSelectVariation={handleSizeSelect}
              onAddToCart={handleAddToCart}
              addToCartLoading={loading}
              hasSizeGuide={!!onOpenSizeGuide && !!sizeGuideText}
              onOpenSizeGuide={handleOpenSizeGuide}
              dropdownHeader={dropdownHeader}
              isSizeFitsSmall={isSizeFitsSmall}
              selectedSchema={selectedSizeSchema}
              onSchemaChange={setSelectedSizeSchema}
              onSizeSelectorOpen={onSizeSelectorOpen}
              onSizeSelectorClose={handleSizeSelectorClose}
              autoOpenSingleSize
              withOverlay
              onBackInStockPopupChange={(isOpen) => {
                setIsBackInStockPopupOpen(isOpen);
                if (!isOpen) {
                  onCloseModal();
                }
              }}
            />
          </Box>

          <Box mt="auto">
            {!loading ? (
              <Button
                variant="primary"
                onClick={() => handleAddToCart()}
                disabled={!size}
                fullWidth
              >
                <Trans>Add to bag</Trans>
              </Button>
            ) : (
              <Button variant="primary" disabled fullWidth>
                <Trans>Adding to bag</Trans>
              </Button>
            )}
          </Box>
        </>
      )}
      {addToCartError && (
        <>
          <Flex justifyContent="space-between">
            <Text
              fontSize={3}
              lineHeight="32px"
              fontWeight="700"
              color="tone.anthraciteDark"
            >
              <Trans>Out of stock</Trans>
            </Text>
            <CloseButton
              onClick={onCloseModal}
              iconSize={24}
              sx={{
                padding: 0,
                justifyContent: "end",
              }}
            />
          </Flex>
          <Box mb={3} pt={2}>
            <Text
              mt={2}
              fontSize={1}
              lineHeight="24px"
              color="tone.anthraciteDark"
            >
              <Trans>
                We’re really sorry, but it seems like this item is out of stock.
              </Trans>
            </Text>
          </Box>
          <Button onClick={onCloseModal} variant="tertiary" fullWidth>
            <Trans>Continue shopping</Trans>
          </Button>
        </>
      )}
    </ModalContainer>
  );
};

export default WishListProductSelector;
